import React, { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Nav from "../components/Nav";
import Footer from "../components/Footer";
import StrategyAudioPanel from "../components/StrategyAudioPanel";
import { api, API_BASE } from "../lib/api";
import { ArrowLeft, ArrowRight, CheckCircle, Question, Lightbulb, Play, Pause, Spinner, SpeakerHigh } from "@phosphor-icons/react";

export default function LearnTopic() {
  const { slug } = useParams();
  const [topic, setTopic] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [audioState, setAudioState] = useState("idle");
  const audioRef = useRef(null);

  useEffect(() => {
    setTopic(null);
    setNotFound(false);
    setAudioState("idle");
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    api
      .get(`/education/${slug}`)
      .then((r) => setTopic(r.data))
      .catch(() => setNotFound(true));
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, [slug]);

  const toggleAudio = () => {
    if (audioState === "playing" && audioRef.current) {
      audioRef.current.pause();
      setAudioState("paused");
      return;
    }
    if (audioState === "paused" && audioRef.current) {
      audioRef.current.play().then(() => setAudioState("playing")).catch(() => setAudioState("idle"));
      return;
    }
    setAudioState("loading");
    const url = `${API_BASE}/education/${slug}/audio`;
    const audio = new Audio(url);
    audioRef.current = audio;
    audio.onplaying = () => setAudioState("playing");
    audio.onpause = () => setAudioState((s) => (s === "playing" ? "paused" : s));
    audio.onended = () => setAudioState("idle");
    audio.onerror = () => setAudioState("idle");
    audio.play().catch(() => setAudioState("idle"));
  };

  if (notFound) {
    return (
      <div className="min-h-screen bg-exodus-bg text-exodus-text">
        <Nav />
        <div className="max-w-3xl mx-auto px-6 pt-44 pb-32 text-center">
          <p className="eyebrow">Topic not found</p>
          <h1 className="font-display text-4xl mt-4">That breakdown isn&apos;t in the library yet.</h1>
          <Link to="/learn" className="btn-gold mt-10">Back to the Library</Link>
        </div>
        <Footer />
      </div>
    );
  }

  if (!topic) {
    return (
      <div className="min-h-screen bg-exodus-bg text-exodus-text">
        <Nav />
        <div className="max-w-3xl mx-auto px-6 pt-44">
          <p className="text-exodus-muted">Loading…</p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid={`learn-topic-${slug}`} className="min-h-screen bg-exodus-bg text-exodus-text">
      <Nav />

      <article className="max-w-4xl mx-auto px-6 lg:px-12 pt-36 pb-16">
        <Link to="/learn" data-testid="back-to-library" className="inline-flex items-center gap-2 text-sm gold-text hover:underline">
          <ArrowLeft size={14} weight="bold" /> Back to the Library
        </Link>
        <p className="eyebrow mt-8"><span className="gold-divider" />{topic.category}</p>
        <h1 className="font-display text-4xl lg:text-6xl mt-4 leading-[0.98]">{topic.title}</h1>
        <p className="text-xl text-exodus-muted mt-8 leading-relaxed font-display italic">{topic.summary}</p>

        <div className="mt-10 flex flex-wrap items-center gap-4">
          <button
            data-testid="learn-listen-btn"
            onClick={toggleAudio}
            className={`inline-flex items-center gap-3 px-5 py-3 border transition-all ${
              audioState === "playing"
                ? "border-exodus-gold bg-exodus-gold text-exodus-bg"
                : "border-exodus-gold text-exodus-gold hover:bg-exodus-gold hover:text-exodus-bg"
            }`}
          >
            {audioState === "loading" ? (
              <Spinner size={18} className="animate-spin" />
            ) : audioState === "playing" ? (
              <Pause size={18} weight="fill" />
            ) : (
              <Play size={18} weight="fill" />
            )}
            <span className="text-xs eyebrow tracking-[3px]">
              {audioState === "loading" ? "Loading audio…" : audioState === "playing" ? "Pause" : audioState === "paused" ? "Resume" : "Listen — 2 min deep-dive"}
            </span>
            <SpeakerHigh size={16} className="ml-1" />
          </button>
        </div>
      </article>

      {/* SIMPLE EXPLAINER */}
      <article className="max-w-4xl mx-auto px-6 lg:px-12 py-10">
        <p className="eyebrow mb-6">The simple version</p>
        <p className="text-lg leading-[1.85] text-exodus-text/90 whitespace-pre-line">{topic.simple_explainer}</p>
      </article>

      {/* WHO IT'S FOR */}
      {topic.who_its_for?.length > 0 && (
        <article className="max-w-4xl mx-auto px-6 lg:px-12 py-10">
          <p className="eyebrow mb-6">Who this is for</p>
          <ul className="space-y-3">
            {topic.who_its_for.map((w, i) => (
              <li key={i} className="flex gap-3 text-exodus-text/90 leading-relaxed">
                <CheckCircle size={20} weight="thin" className="text-exodus-gold mt-1 shrink-0" />
                <span>{w}</span>
              </li>
            ))}
          </ul>
        </article>
      )}

      {/* HOW IT WORKS */}
      {topic.how_it_works?.length > 0 && (
        <article className="max-w-4xl mx-auto px-6 lg:px-12 py-10">
          <p className="eyebrow mb-6">How it works</p>
          <div className="space-y-px bg-exodus-border">
            {topic.how_it_works.map((step, i) => (
              <div key={i} className="bg-exodus-bg p-6 lg:p-8 flex gap-6">
                <p className="font-mono text-sm gold-text shrink-0">{String(i + 1).padStart(2, "0")}</p>
                <p className="text-exodus-text/90 leading-relaxed">{step}</p>
              </div>
            ))}
          </div>
        </article>
      )}

      {/* BENEFITS */}
      {topic.benefits?.length > 0 && (
        <article className="max-w-4xl mx-auto px-6 lg:px-12 py-10">
          <p className="eyebrow mb-6">The benefits</p>
          <div className="grid sm:grid-cols-2 gap-4">
            {topic.benefits.map((b, i) => (
              <div key={i} className="card-surface p-5 flex gap-3">
                <Lightbulb size={20} weight="thin" className="text-exodus-gold mt-0.5 shrink-0" />
                <p className="text-sm text-exodus-text/90 leading-relaxed">{b}</p>
              </div>
            ))}
          </div>
        </article>
      )}

      {/* MISCONCEPTIONS */}
      {topic.common_misconceptions?.length > 0 && (
        <article className="max-w-4xl mx-auto px-6 lg:px-12 py-10">
          <p className="eyebrow mb-6">Common misconceptions</p>
          <div className="space-y-3">
            {topic.common_misconceptions.map((m, i) => (
              <div key={i} className="card-surface p-5 flex gap-3">
                <Question size={20} weight="thin" className="text-exodus-gold mt-0.5 shrink-0" />
                <p className="text-sm text-exodus-text/90 leading-relaxed">{m}</p>
              </div>
            ))}
          </div>
        </article>
      )}

      {/* KEY TAKEAWAY */}
      {topic.key_takeaway && (
        <article className="max-w-4xl mx-auto px-6 lg:px-12 py-10">
          <div className="border-l-2 border-exodus-gold pl-6 lg:pl-10">
            <p className="eyebrow mb-3">The takeaway</p>
            <p className="font-display text-2xl lg:text-3xl leading-snug italic">{topic.key_takeaway}</p>
          </div>
        </article>
      )}

      {/* STRATEGY COMPANION — age-aware 3-min implementation audio */}
      <article className="max-w-4xl mx-auto px-6 lg:px-12">
        <StrategyAudioPanel
          sourcePath={`/education/${slug}/strategy-audio`}
          testIdPrefix={`learn-strategy-${slug}`}
        />
      </article>

      {/* SOFT CTA */}
      <section className="max-w-4xl mx-auto px-6 lg:px-12 py-20 mt-10 border-t border-exodus-border">
        <p className="eyebrow">Want to go deeper?</p>
        <h3 className="font-display text-3xl lg:text-4xl mt-3 leading-tight">
          A 30-minute private session walks this through with your numbers.
        </h3>
        <p className="text-exodus-muted mt-4 leading-relaxed max-w-xl">
          Reserve your seat with a $100 refundable deposit. If the strategy isn&apos;t right for you, the deposit comes back.
        </p>
        <div className="flex flex-wrap gap-4 mt-8">
          <Link to="/reserve" className="btn-gold" data-testid="topic-reserve">
            Reserve a Session <ArrowRight size={16} weight="bold" />
          </Link>
          <Link to="/learn" className="btn-ghost" data-testid="topic-back-library">Keep Reading</Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
